import WorkspaceToolbar from '@/components/admin/ordersWorkspace/WorkspaceToolbar';
import ItemGroupsSection from '@/components/admin/ordersWorkspace/ItemGroupsSection';
import OrderGroupsSection from '@/components/admin/ordersWorkspace/OrderGroupsSection';
import type {
  AdminWorkspaceCurrencyCode,
  AdminWorkspaceItemGroupMode,
  AdminWorkspaceItemState,
  AdminWorkspaceMode,
  AdminWorkspaceOrderAction,
  AdminWorkspaceOrderState,
  AdminWorkspacePreviewMeta,
} from '@/components/admin/ordersWorkspace/types';
import type { MeasurementUnit } from '@/types/measurement';
import type { AdminOrderItemStatus } from '@/services/adminService';

export type {
  AdminWorkspaceItemGroup,
  AdminWorkspaceOrderGroup,
  AdminWorkspaceMode,
  AdminWorkspaceOrderAction,
  AdminWorkspacePreviewMeta,
} from '@/components/admin/ordersWorkspace/types';
export { ORDER_ITEM_STATUSES } from '@/types/orderStatus';

interface AdminOrdersWorkspaceProps {
  mode: AdminWorkspaceMode;
  onModeChange: (mode: AdminWorkspaceMode) => void;
  measurementUnit: MeasurementUnit;
  onMeasurementUnitChange: (unit: MeasurementUnit) => void;
  currencyCode: AdminWorkspaceCurrencyCode;
  onCurrencyCodeChange: (currencyCode: AdminWorkspaceCurrencyCode) => void;
  usdToUahRate: number | null;
  itemState: AdminWorkspaceItemState;
  orderState: AdminWorkspaceOrderState;
  onRefreshItems: () => void;
  onRefreshOrders: () => void;
  onResetFilters: () => void;
  onItemGroupModeChange: (mode: AdminWorkspaceItemGroupMode) => void;
  onItemSearchInputChange: (value: string) => void;
  onItemDateFromChange: (value: string) => void;
  onItemDateToChange: (value: string) => void;
  onItemOnlyFirstOrderChange: (value: boolean) => void;
  onToggleItemStatusFilter: (status: AdminOrderItemStatus) => void;
  onExportAllItems: () => void;
  onExportItemGroup: Parameters<typeof ItemGroupsSection>[0]['onExportGroup'];
  onExportItemGroupDpiChange: Parameters<typeof ItemGroupsSection>[0]['onExportDpiChange'];
  onItemStatusChange: Parameters<typeof ItemGroupsSection>[0]['onItemStatusChange'];
  onDownloadItemWithMargin: Parameters<typeof ItemGroupsSection>[0]['onDownloadItemWithMargin'];
  reuploadDialogItem: Parameters<typeof ItemGroupsSection>[0]['reuploadDialogItem'];
  reuploadComment: Parameters<typeof ItemGroupsSection>[0]['reuploadComment'];
  reuploadSubmitting: Parameters<typeof ItemGroupsSection>[0]['reuploadSubmitting'];
  onReuploadCommentChange: Parameters<typeof ItemGroupsSection>[0]['onReuploadCommentChange'];
  onReuploadDialogConfirm: Parameters<typeof ItemGroupsSection>[0]['onReuploadDialogConfirm'];
  onReuploadDialogOpenChange: Parameters<typeof ItemGroupsSection>[0]['onReuploadDialogOpenChange'];
  onOpenPreview: (url: string | null, meta?: AdminWorkspacePreviewMeta | null) => void;
  onOpenOrder: (displayCode: string) => void;
  onOpenOrderItems: (displayCode: string) => void;
  onOrderSearchInputChange: (value: string) => void;
  onOrderDateFromChange: (value: string) => void;
  onOrderDateToChange: (value: string) => void;
  onToggleOrderStatusVisibility: (status: string) => void;
  onOrderGroupPrintLabels: (statusKey: string, count: number) => void;
  onOrderStatusChange: (orderId: string, status: 'PAID' | 'IN_PRODUCTION' | 'PRINTED' | 'IN_DELIVERY' | 'DELIVERED') => void;
  onOrderAction: (action: AdminWorkspaceOrderAction, orderId: string) => void;
}

const AdminOrdersWorkspace = ({
  mode,
  onModeChange,
  measurementUnit,
  onMeasurementUnitChange,
  currencyCode,
  onCurrencyCodeChange,
  usdToUahRate,
  itemState,
  orderState,
  onRefreshItems,
  onRefreshOrders,
  onResetFilters,
  onItemGroupModeChange,
  onItemSearchInputChange,
  onItemDateFromChange,
  onItemDateToChange,
  onItemOnlyFirstOrderChange,
  onToggleItemStatusFilter,
  onExportAllItems,
  onExportItemGroup,
  onExportItemGroupDpiChange,
  onItemStatusChange,
  onDownloadItemWithMargin,
  reuploadDialogItem,
  reuploadComment,
  reuploadSubmitting,
  onReuploadCommentChange,
  onReuploadDialogConfirm,
  onReuploadDialogOpenChange,
  onOpenPreview,
  onOpenOrder,
  onOpenOrderItems,
  onOrderSearchInputChange,
  onOrderDateFromChange,
  onOrderDateToChange,
  onToggleOrderStatusVisibility,
  onOrderGroupPrintLabels,
  onOrderStatusChange,
  onOrderAction,
}: AdminOrdersWorkspaceProps) => {
  const isItemsMode = mode === 'items';

  return (
    <div className="space-y-8">
      <WorkspaceToolbar
        mode={mode}
        onModeChange={onModeChange}
        measurementUnit={measurementUnit}
        onMeasurementUnitChange={onMeasurementUnitChange}
        currencyCode={currencyCode}
        onCurrencyCodeChange={onCurrencyCodeChange}
        itemState={itemState}
        orderState={orderState}
        onRefreshItems={onRefreshItems}
        onRefreshOrders={onRefreshOrders}
        onResetFilters={onResetFilters}
        onItemGroupModeChange={onItemGroupModeChange}
        onItemSearchInputChange={onItemSearchInputChange}
        onItemDateFromChange={onItemDateFromChange}
        onItemDateToChange={onItemDateToChange}
        onItemOnlyFirstOrderChange={onItemOnlyFirstOrderChange}
        onToggleItemStatusFilter={onToggleItemStatusFilter}
        onExportAllItems={onExportAllItems}
        onOrderSearchInputChange={onOrderSearchInputChange}
        onOrderDateFromChange={onOrderDateFromChange}
        onOrderDateToChange={onOrderDateToChange}
        onToggleOrderStatusVisibility={onToggleOrderStatusVisibility}
      />

      {isItemsMode ? (
        <ItemGroupsSection
          groups={itemState.groupedItems}
          status={itemState.status}
          measurementUnit={measurementUnit}
          currencyCode={currencyCode}
          usdToUahRate={usdToUahRate}
          updatingIds={itemState.updatingIds}
          exportingGroupKeys={itemState.exportingGroupKeys}
          exportDpiByGroupKey={itemState.exportDpiByGroupKey}
          downloadingWithMarginIds={itemState.downloadingWithMarginIds}
          onExportGroup={onExportItemGroup}
          onExportDpiChange={onExportItemGroupDpiChange}
          onItemStatusChange={onItemStatusChange}
          onDownloadItemWithMargin={onDownloadItemWithMargin}
          reuploadDialogItem={reuploadDialogItem}
          reuploadComment={reuploadComment}
          reuploadSubmitting={reuploadSubmitting}
          onReuploadCommentChange={onReuploadCommentChange}
          onReuploadDialogConfirm={onReuploadDialogConfirm}
          onReuploadDialogOpenChange={onReuploadDialogOpenChange}
          onOpenPreview={onOpenPreview}
          onOpenOrder={onOpenOrder}
        />
      ) : (
        <OrderGroupsSection
          groups={orderState.groupedOrders}
          status={orderState.status}
          currencyCode={currencyCode}
          usdToUahRate={usdToUahRate}
          orderStatusSavingId={orderState.orderStatusSavingId}
          onOrderGroupPrintLabels={onOrderGroupPrintLabels}
          onOrderStatusChange={onOrderStatusChange}
          onOrderAction={onOrderAction}
          onOpenOrderItems={onOpenOrderItems}
        />
      )}
    </div>
  );
};

export default AdminOrdersWorkspace;
